/**
 * SIM-1678 — pure schema-connection helpers. The data model stores tenant-authored table and field
 * definitions with UNQUALIFIED names while the warehouse panel returns the qualified
 * `dataset.table.column` shape, so these walk the schema once and answer where a name appears,
 * which direction a mismatch runs, and which defs reference a table. `buildSchemaIndex` allocates.
 */
import type { AgentFieldDefinition, AgentTableDefinition, WarehouseSchema } from "./types";

// ─── Public types ───────────────────────────────────────────────────────────

export interface QualifiedTableRef {
  datasetId: string;
  tableName: string;
}

export interface QualifiedColumnRef {
  datasetId: string;
  tableName: string;
  columnName: string;
  type: string;
  mode: string;
}

/**
 * Pre-computed lookup tables over the warehouse schema. Built once per call to
 * `buildSchemaIndex` and reused by the other helpers in this module.
 */
export interface SchemaIndex {
  /** `tableName` → all qualified locations it appears in (byte-exact). */
  tablesByName: Map<string, QualifiedTableRef[]>;
  /**
   * SIM-1729 — `normalizeTableName(tableName)` → all qualified locations, cross-walking Simplicity
   * pipeline prefix/suffix variants (`sim__`, `ps__`, `__latest`, …) so an author writing
   * `attendance` matches every `*__attendance__*` table. Byte-exact `tablesByName` is preserved
   * for callers that need it.
   */
  tablesByNormalizedName: Map<string, QualifiedTableRef[]>;
  /** `columnName` → all qualified locations (dataset+table+column) it appears in. */
  columnsByName: Map<string, QualifiedColumnRef[]>;
  /** `${datasetId}|${tableName}|${columnName}` → the matching qualified column. */
  columnByTableAndName: Map<string, QualifiedColumnRef>;
}

/**
 * Row-level view of a Field def associated with a specific table, used to
 * render the Tables row expansion. Carries the per-table description override
 * and the per-pair connection state.
 */
export interface AssociatedField {
  /** AgentFieldDefinition.id */
  id: string;
  /** AgentFieldDefinition.fieldName (unqualified column name) */
  fieldName: string;
  /** Top-level body.description (fallback when no perTable override). */
  description: string;
  /** body.perTable[tableName] — what the field def says about this specific table. */
  perTableDescription: string;
  /**
   * Per-pair connection state for (fieldName, tableName) — present iff the
   * warehouse has at least one table named `tableName` containing a column
   * named `fieldName`. The matching ref is the first one found.
   */
  inSchemaAt: QualifiedTableRef | null;
}

export interface MissingContextCounts {
  /** T2 table defs whose `tableName` does not appear in the warehouse. */
  orphanTables: number;
  /** T2 field defs whose `fieldName` does not appear anywhere in the warehouse. */
  orphanFields: number;
  /** Warehouse tables not referenced by any T2 table def. */
  unmappedTables: number;
  /** Warehouse columns not referenced by any T2 field def. */
  unmappedColumns: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function asPerTableMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = v;
  }
  return out;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function columnKey(datasetId: string, tableName: string, columnName: string): string {
  return `${datasetId}|${tableName}|${columnName}`;
}

// ─── Name normalization (SIM-1729) ──────────────────────────────────────────

/**
 * SIM-1729 — Simplicity pipeline prefixes stripped during normalization. Authors write natural
 * names like `attendance`; the warehouse stores `ps__attendance__latest` and friends. Cross-walks
 * compare the normalized form, so the unmapped-tables list shows one row per concept rather than
 * one per pipeline variant.
 */
const STRIPPABLE_PREFIXES = ["sim__", "ps__", "gs__", "sm__", "iready__"] as const;
const STRIPPABLE_SUFFIXES = ["__latest", "__snapshots", "__gsheets", "__history"] as const;

/**
 * Normalize a table name by stripping known pipeline prefixes and suffixes, returning the input
 * unchanged if that would empty it. Deterministic — no case-folding, no edit distance — so two
 * inputs normalize alike IFF they are the same concept: `ps__attendance__latest` and
 * `sim__attendance__latest` both give `attendance`, but `incident_action` stays distinct.
 */
export function normalizeTableName(name: string): string {
  let n = name;
  let changed = true;
  while (changed) {
    changed = false;
    for (const p of STRIPPABLE_PREFIXES) {
      if (n.startsWith(p) && n.length > p.length) {
        n = n.slice(p.length);
        changed = true;
        break;
      }
    }
    for (const s of STRIPPABLE_SUFFIXES) {
      if (n.endsWith(s) && n.length > s.length) {
        n = n.slice(0, n.length - s.length);
        changed = true;
        break;
      }
    }
  }
  return n.length > 0 ? n : name;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Walk the warehouse schema once and build name → qualified-ref lookups.
 */
export function buildSchemaIndex(schema: WarehouseSchema): SchemaIndex {
  const tablesByName = new Map<string, QualifiedTableRef[]>();
  const tablesByNormalizedName = new Map<string, QualifiedTableRef[]>();
  const columnsByName = new Map<string, QualifiedColumnRef[]>();
  const columnByTableAndName = new Map<string, QualifiedColumnRef>();

  for (const ds of schema.datasets) {
    for (const t of ds.tables) {
      const tableRef: QualifiedTableRef = { datasetId: ds.datasetId, tableName: t.tableName };
      const existingTables = tablesByName.get(t.tableName) ?? [];
      existingTables.push(tableRef);
      tablesByName.set(t.tableName, existingTables);

      const normalized = normalizeTableName(t.tableName);
      const existingNormalized = tablesByNormalizedName.get(normalized) ?? [];
      existingNormalized.push(tableRef);
      tablesByNormalizedName.set(normalized, existingNormalized);

      for (const c of t.columns) {
        const colRef: QualifiedColumnRef = {
          datasetId: ds.datasetId,
          tableName: t.tableName,
          columnName: c.name,
          type: c.type,
          mode: c.mode,
        };
        const existingCols = columnsByName.get(c.name) ?? [];
        existingCols.push(colRef);
        columnsByName.set(c.name, existingCols);
        columnByTableAndName.set(columnKey(ds.datasetId, t.tableName, c.name), colRef);
      }
    }
  }

  return { tablesByName, tablesByNormalizedName, columnsByName, columnByTableAndName };
}

/**
 * Every qualified location where the normalized `tableName` appears. An empty array means the table
 * is an orphan — absent from the warehouse under any Simplicity prefix/suffix variant. Matching is
 * via normalization, so author-side `attendance` finds `ps__attendance__latest` (SIM-1729).
 */
export function findTableMatches(tableName: string, index: SchemaIndex): QualifiedTableRef[] {
  return index.tablesByNormalizedName.get(normalizeTableName(tableName)) ?? [];
}

/**
 * Per-pair lookup: returns the qualified ref of the warehouse table when the
 * column is present in any warehouse table whose name normalizes to the same
 * form as `tableName`. Returns the first match (deterministic by walk order);
 * null if no match.
 */
export function findFieldInTable(
  fieldName: string,
  tableName: string,
  index: SchemaIndex,
): QualifiedTableRef | null {
  const tables = index.tablesByNormalizedName.get(normalizeTableName(tableName));
  if (!tables) return null;
  for (const t of tables) {
    if (index.columnByTableAndName.has(columnKey(t.datasetId, t.tableName, fieldName))) {
      return t;
    }
  }
  return null;
}

/**
 * Returns every qualified column matching the unqualified `fieldName`, across
 * all tables in the warehouse. Empty array = orphan.
 */
export function findFieldMatches(fieldName: string, index: SchemaIndex): QualifiedColumnRef[] {
  return index.columnsByName.get(fieldName) ?? [];
}

/**
 * Field defs whose `body.perTable` map contains a key matching `tableName`.
 * The `perTableDescription` is the value at that key; `inSchemaAt` is the
 * qualified warehouse ref iff the column exists there.
 */
export function associatedFieldsForTable(
  tableName: string,
  t2Fields: AgentFieldDefinition[],
  index: SchemaIndex,
): AssociatedField[] {
  const out: AssociatedField[] = [];
  for (const fd of t2Fields) {
    const perTable = asPerTableMap(fd.body.perTable);
    if (!Object.prototype.hasOwnProperty.call(perTable, tableName)) continue;
    out.push({
      id: fd.id,
      fieldName: fd.fieldName,
      description: asString(fd.body.description),
      perTableDescription: perTable[tableName] ?? "",
      inSchemaAt: findFieldInTable(fd.fieldName, tableName, index),
    });
  }
  return out;
}

/**
 * Aggregate counts for the four "missing context" buckets surfaced as header
 * chips on the page.
 */
export function missingContextCounts(
  t2Tables: AgentTableDefinition[],
  t2Fields: AgentFieldDefinition[],
  index: SchemaIndex,
): MissingContextCounts {
  // Tables: compare via SIM-1729 normalization. Fields: byte-exact (field-name
  // normalization is deferred — see SIM-1729 ticket "out of scope").
  let orphanTables = 0;
  for (const t of t2Tables) {
    if (!index.tablesByNormalizedName.has(normalizeTableName(t.tableName))) orphanTables += 1;
  }
  let orphanFields = 0;
  for (const f of t2Fields) {
    if (!index.columnsByName.has(f.fieldName)) orphanFields += 1;
  }

  const t2NormalizedTableNames = new Set(t2Tables.map((t) => normalizeTableName(t.tableName)));
  const t2FieldNames = new Set(t2Fields.map((f) => f.fieldName));

  let unmappedTables = 0;
  for (const name of index.tablesByNormalizedName.keys()) {
    if (!t2NormalizedTableNames.has(name)) unmappedTables += 1;
  }
  let unmappedColumns = 0;
  for (const name of index.columnsByName.keys()) {
    if (!t2FieldNames.has(name)) unmappedColumns += 1;
  }

  return { orphanTables, orphanFields, unmappedTables, unmappedColumns };
}

/**
 * SIM-1679 — warehouse tables not referenced by any T2 table def, for the Tables tab's
 * missing-context crosswalk; clicking one opens the create sheet pre-filled with the unqualified
 * name. One row per unique unqualified `tableName`, deduped across datasets, with `locations`
 * carrying every dataset the name appears in so the crosswalk can disambiguate. Sorted by name.
 */
export function listUnmappedTables(
  t2Tables: AgentTableDefinition[],
  index: SchemaIndex,
): Array<{ tableName: string; locations: QualifiedTableRef[] }> {
  // SIM-1729: compare normalized forms. The row's `tableName` is the
  // normalized concept name (what the author would type in T2); `locations`
  // lists every pipeline variant for disambiguation.
  const t2Normalized = new Set(t2Tables.map((t) => normalizeTableName(t.tableName)));
  const out: Array<{ tableName: string; locations: QualifiedTableRef[] }> = [];
  for (const [normalized, locations] of index.tablesByNormalizedName.entries()) {
    if (t2Normalized.has(normalized)) continue;
    out.push({ tableName: normalized, locations });
  }
  out.sort((a, b) => a.tableName.localeCompare(b.tableName));
  return out;
}

/**
 * SIM-1679 — warehouse columns NOT referenced by any T2 field def. Same shape as
 * `listUnmappedTables` but at the column level, returning ALL unmapped (name, datasetId, tableName)
 * triples — a column name in 5 unmapped tables yields 5 entries — so the crosswalk can show each
 * specific location. Sorted by (tableName, columnName) so columns of one table cluster.
 */
export function listUnmappedFields(
  t2Fields: AgentFieldDefinition[],
  index: SchemaIndex,
): QualifiedColumnRef[] {
  const t2Names = new Set(t2Fields.map((f) => f.fieldName));
  const out: QualifiedColumnRef[] = [];
  for (const [name, refs] of index.columnsByName.entries()) {
    if (t2Names.has(name)) continue;
    for (const r of refs) out.push(r);
  }
  out.sort((a, b) => {
    const t = a.tableName.localeCompare(b.tableName);
    return t !== 0 ? t : a.columnName.localeCompare(b.columnName);
  });
  return out;
}
